<template>
    <div>
        <div class="row">
            <div class="col-md-12 mb-3">
                <h1 class="mb-0">Clinic Hours</h1>
            </div>
        </div>
        <div>
            <div class="card">
                <div class="card-body">
                    <LoaderList v-if="isLoading" class="w-100" />
                    <div v-else>
                        <p class="timeline-paragraph-width">
                            Set up your clinic’s operating hours for each day of
                            the week. You can create multiple time blocks to
                            define when your clinic is open as ‘regular hours’
                            or operating as ‘after hours’ for price markup
                            purposes throughout the 24 hour cycle of the day.
                            Time ranges with no block are defined as hours when
                            the clinic is fully closed and not operational. If
                            the clinic is fully closed for an entire day, use
                            the toggle switch to disable the day.
                        </p>

                        <div class="overflow-y-scroll" ref="scrollContainer">
                            <WeekTimeline ref="weekTimeline">
                                <DayTimeline
                                    v-for="day in clinicDays"
                                    :key="day.day"
                                    :day="day"
                                    @toggle="onToggle"
                                    @add="addEvent(day)"
                                >
                                    <BlockTimeline
                                        v-for="event in day.clinic_hours"
                                        :key="event.id"
                                        :event="event"
                                        :start="event.start"
                                        :end="event.end"
                                        :variant="
                                            event.is_after_hour
                                                ? 'primary'
                                                : 'regular'
                                        "
                                        @edit="editEvent(day, event)"
                                        @delete="deleteEvent(day, event)"
                                    />
                                </DayTimeline>
                            </WeekTimeline>
                        </div>

                        <div class="mt-3 d-flex d-flex-gap">
                            <div class="d-flex">
                                <div class="legend-box"></div>
                                <div class="legend-box-label">Regular Hour</div>
                            </div>
                            <div class="d-flex align-items-center">
                                <div
                                    class="legend-box legend-box-after-hour"
                                ></div>
                                <div class="legend-box-label">After Hour</div>
                            </div>
                        </div>

                        <div
                            v-if="isTimelineOverflow"
                            class="tip-text text-muted mt-3"
                        >
                            Note: This timeline contains more information. Use
                            your mouse or touch to scroll horizontally to view
                            more.
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <ModalCreateUpdate
            :clinic-days="clinicDays"
            ref="modalCreateUpdate"
            @before-save="onBeforeSave"
            @success="onSuccessSave"
            @closed="onDayModalClosed"
        />
    </div>
</template>

<script>
import LoaderList from "@/components/elements/loader-list";
import ClinicHourService from "@/services/setting/clinic-hour";
import WeekTimeline from "@/components/setting-clinic-hour-v2/week-timeline/week-timeline";
import DayTimeline from "@/components/setting-clinic-hour-v2/week-timeline/week-timeline-day";
import BlockTimeline from "@/components/setting-clinic-hour-v2/week-timeline/week-timeline-block";
import ModalCreateUpdate from "@/components/setting-clinic-hour-v2/modal-create-update";

export default {
    components: {
        LoaderList,
        WeekTimeline,
        DayTimeline,
        BlockTimeline,
        ModalCreateUpdate
    },
    data() {
        return {
            isLoading: false,
            clinicDays: [],
            isTimelineOverflow: false
        };
    },
    watch: {},
    computed: {},
    async mounted() {
        await this.getClinicHours();

        this.checkTimelineOverflow();
        window.addEventListener("resize", this.checkTimelineOverflow);
    },
    beforeDestroy() {
        window.removeEventListener("resize", this.checkTimelineOverflow);
    },
    methods: {
        async getClinicHours() {
            this.isLoading = true;

            try {
                const { response } = await ClinicHourService.getClinicHoursV2();

                if (response?.code === 200) {
                    this.clinicDays = response.resources;

                    // move sunday to the last order
                    const [sunday, ...restOfDays] = this.clinicDays;
                    this.clinicDays = [...restOfDays, sunday];
                } else {
                    throw new Error(
                        response.message || "Unable to fetch clinic hours"
                    );
                }
            } catch (error) {
                this._message(
                    "Error : Unable to get the clinic hours list.",
                    "danger"
                );
                console.error(error);
            } finally {
                this.isLoading = false;
            }
        },
        async onToggle(day) {
            if (!day.clinic_hours.length && day.isactive) {
                this.addEvent(day);
                return;
            }

            await this.toggleClinicDay(day);
        },
        async toggleClinicDay(day, isactive = null) {
            try {
                if (isactive === null) {
                    isactive = day.isactive;
                }

                const { response } = await ClinicHourService.toggleClinicDay(
                    day.id,
                    isactive ? 1 : 0
                );

                if (response?.code === 200) {
                    if (isactive) {
                        this._message("Clinic day successfully enabled.");
                    } else {
                        this._message("Clinic day successfully disabled.");
                    }
                } else {
                    throw new Error(
                        response.message || "Unable to update clinic day"
                    );
                }
            } catch (error) {
                this._message("Error : Unable to update clinic day.", "danger");
                console.error(error);
            }
        },
        addEvent(day) {
            this.$refs.modalCreateUpdate.open(day);
        },
        editEvent(day, event) {
            this.$refs.modalCreateUpdate.open(day, event);
        },
        async deleteEvent(day, event) {
            await this.$confirmation({
                message:
                    "Are you sure you want to delete this time block? This action cannot be undone. If this block is marked as after hours, removing it may affect inventory pricing and online booking fees.",
                title: "Delete Booking Hours block",
                props: {
                    confirmBtnTxt: "Delete"
                },
                preConfirm: async () => {
                    await this.deleteHour(event, day);
                }
            });
        },
        async deleteHour(hour, day) {
            const isDisableDeleteHour =
                !hour.is_after_hour &&
                day.clinic_hours.filter(clinicHour => !clinicHour.is_after_hour)
                    .length <= 1;

            if (isDisableDeleteHour) {
                this._message(
                    "Open days must have at least one 'Regular Hours' block. Edit it or close the day to remove.",
                    "danger"
                );
                return;
            }

            try {
                const { response } = await ClinicHourService.deleteClinicHour(
                    hour.id
                );

                if (response?.code === 200) {
                    this._message("Clinic time block successfully deleted.");

                    // disable the day if it has no more hour blocks
                    if (day.clinic_hours.length <= 1) {
                        await this.toggleClinicDay(day, false);
                    }

                    await this.getClinicHours();
                } else {
                    throw new Error(
                        response.message || "Unable to delete time block."
                    );
                }
            } catch (error) {
                this._message("Error : Unable to delete time block.", "danger");
                console.error(error);
            }
        },
        checkTimelineOverflow() {
            this.$nextTick(() => {
                const wrapper = this.$refs.scrollContainer;
                const content = this.$refs.weekTimeline.$el;
                if (wrapper && content) {
                    this.isTimelineOverflow =
                        content.scrollWidth > wrapper.clientWidth;
                }
            });
        },
        async onBeforeSave(day) {
            // enable the day if it add a new hour block
            if (!day.clinic_hours.length && day.isactive) {
                await this.toggleClinicDay(day, true);
            }
        },
        async onSuccessSave() {
            await this.getClinicHours();
        },
        onDayModalClosed(day) {
            // if add event is not success, reset the toggle active
            if (!day.clinic_hours.length && day.isactive) {
                this.clinicDays = this.clinicDays.map(clinicDay => {
                    if (clinicDay.id === day.id) {
                        return { ...clinicDay, isactive: false };
                    }
                    return clinicDay;
                });
            }
        }
    }
};
</script>

<style scoped>
.overflow-y-scroll {
    overflow-y: scroll;
}

.d-flex-gap {
    gap: 30px;
}

.legend-box {
    width: 30px;
    height: 15px;
    background-color: #67961c;
}

.legend-box-after-hour {
    background-color: #7a0000;
}

.legend-box-label {
    font-size: 0.8em;
    margin-left: 0.5em;
}
.tip-text {
    font-weight: 300;
    vertical-align: baseline;
    font-size: smaller;
}

.timeline-paragraph-width {
    max-width: 1000px;
}
</style>
